import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonSiguiente, Input } from "@/components/ui";
import { safeFetch } from "@/api/safeFetch";
import Alerta from "@/components/alert/Alerta";
import { AfiliadoCodigo, AlertaType } from "@/types";
import { PopupEditarAfiliado } from "@/components/popup";

export default function BajaAfiliados() {
    const navigate = useNavigate();
    const token = localStorage.getItem("token");

    // ======================
    // Estados
    // ======================
    const [busqueda, setBusqueda] = useState("");
    const [loadingBuscar, setLoadingBuscar] = useState(false);
    const [error, setError] = useState("");

    const [afiliado, setAfiliado] = useState<AfiliadoCodigo | null>(null);
    const [loadingBaja, setLoadingBaja] = useState(false);
    const [alerta, setAlerta] = useState<{ mensaje: string; type: AlertaType } | null>(null);

    const [modoPopup, setModoPopup] = useState<"baja" | "reactivar" | null>(null);

    // ======================
    // Buscar afiliado
    // ======================
    const handleBuscar = async () => {
        if (!busqueda.trim()) return;
      
        const controller = new AbortController();
        const signal = controller.signal;
      
        setLoadingBuscar(true);
        setError("");
        setAfiliado(null);
      
        try {
          const res = await safeFetch(
            `/api/afiliados/buscar?query=${encodeURIComponent(busqueda.trim())}`,
            { headers: { Authorization: `Bearer ${token}` }, signal }
          );
      
          if (!res.ok) throw new Error("Afiliado no encontrado");
      
          const data: AfiliadoCodigo = await res.json();
          setAfiliado(data);
        } catch (err: any) {
          if (err.name !== "AbortError") {
            setError(err.message || "Error buscando afiliado");
          }
        } finally {
          setLoadingBuscar(false);
        }
      
        return () => controller.abort();
      };
      

    // ======================
    // Dar de baja
    // ======================
    const handleBaja = async () => {
        if (!afiliado?.id) return;
        setLoadingBaja(true);

        try {
            const res = await safeFetch(
                `/api/afiliados/baja/${afiliado.id}`,
                { method: "PUT", headers: { Authorization: `Bearer ${token}` } }
            );

            if (!res.ok) throw new Error("No se pudo dar de baja al afiliado");

            setAlerta({ mensaje: "Afiliado dado de baja correctamente ✅", type: "success" });
            setAfiliado({ ...afiliado, activo: 0 }); // actualizar estado local
        } catch (err) {
            setAlerta({ mensaje: err instanceof Error ? err.message : "Error en la baja", type: "error" });
        } finally {
            setLoadingBaja(false);
            setModoPopup(null);
        }
    };

    // ======================
    // Reactivar
    // ======================
    const handleReactivar = async () => {
        if (!afiliado?.id) return;
        setLoadingBaja(true);

        try {
            const res = await safeFetch(
                `/api/afiliados/reactivar/${afiliado.id}`,
                { method: "PUT", headers: { Authorization: `Bearer ${token}` } }
            );

            if (!res.ok) throw new Error("No se pudo reactivar el afiliado");

            setAlerta({ mensaje: "Afiliado reactivado correctamente ✅", type: "success" });
            setAfiliado({ ...afiliado, activo: 1 }); // actualizar estado local
        } catch (err) {
            setAlerta({ mensaje: err instanceof Error ? err.message : "Error al reactivar", type: "error" });
        } finally {
            setLoadingBaja(false);
            setModoPopup(null);
        }
    };

    useEffect(() => {
        return () => {
          setBusqueda("");
          setAfiliado(null);
          setError("");
          setLoadingBuscar(false);
          setLoadingBaja(false);
          setAlerta(null);
          setModoPopup(null);
        };
      }, []);
      

    return (
    <div className="w-full min-h-screen flex items-center justify-center bg-zinc-500 p-4 relative">
              
              {/* 🔙 Botón volver arriba izquierda */}
            <div className="absolute top-4 left-4">
                <ButtonSiguiente
                    onClick={() => navigate("/afiliados")}
                    className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition shadow"
                >
                    ⬅ Volver
                </ButtonSiguiente>
            </div>

            <div className="w-full h-full max-w-2xl bg-zinc-400 dark:text-zinc-700 p-4 sm:p-6 rounded-xl shadow-md border-zinc-600 border-r-4 border-t-2 shadow-red-500 flex flex-col gap-6">
                <h2 className="text-xl sm:text-2xl text-center font-bold">Dar de baja / Reactivar Afiliado</h2>

                {/* BUSCADOR */}
                <div className="w-full bg-red-600 p-4 rounded-xl flex flex-col md:flex-row gap-4 items-stretch border-white border border-r-4 border-t-2 shadow-md shadow-red-500">
                    <Input
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        placeholder="Buscar por DNI, NIE, código o nombre"
                        className="h-10 px-3 rounded w-full md:w-1/2"
                    />
                    <ButtonSiguiente
                        className="!h-9 px-6 bg-red-600 text-white rounded w-full md:w-1/2"
                        onClick={handleBuscar}
                        disabled={loadingBuscar}
                    >
                        {loadingBuscar ? "Buscando..." : "Buscar"}
                    </ButtonSiguiente>
                </div>

                {error && <p className="text-red-600 text-center">{error}</p>}

                {/* DATOS COMPLETOS DEL AFILIADO */}
                {afiliado && (
                    <div className="grid grid-cols-12 gap-2 border p-4 rounded bg-white shadow-md">
                        <p className="col-span-12 font-semibold">Nombre: {afiliado.nombre}</p>
                        <p className="col-span-12 font-semibold">Apellidos: {afiliado.apellidos}</p>
                        <p className="col-span-6 font-semibold">DNI/NIE: {afiliado.dni || afiliado.nie}</p>
                        <p className="col-span-6 font-semibold">Código: {afiliado.codigo}</p>
                        <p className="col-span-12 font-semibold">Correo: {afiliado.correo_electronico}</p>
                        <p className="col-span-6 font-semibold">Teléfono: {afiliado.numero_telefono}</p>
                        <p className="col-span-12 font-semibold">Dirección:</p>
                        <p className="col-span-12 ml-4">{afiliado.direccion.calle}, {afiliado.direccion.localidad}, {afiliado.direccion.provincia}</p>
                        <p className="col-span-6 ml-4">CP: {afiliado.direccion.codigo_postal}</p>
                        <p className="col-span-6 ml-4">País: {afiliado.direccion.pais}</p>

                        {/* ===== Estado ===== */}
                        <p className="col-span-12 font-semibold mt-2">
                            Estado: <span className={afiliado.activo === 1 ? "text-green-600" : "text-red-600"}>
                                {afiliado.activo === 1 ? "Activo ✅" : "Inactivo ❌"}
                            </span>
                        </p>

                        <ButtonSiguiente
                            type="button"
                            className="col-span-12 bg-red-600 text-white p-3 rounded hover:bg-red-700 transition mt-2"
                            onClick={() => setModoPopup(afiliado.activo === 1 ? "baja" : "reactivar")}
                            disabled={loadingBaja}
                        >
                            {afiliado.activo === 1 ? "Dar de baja" : "Reactivar"}
                        </ButtonSiguiente>
                    </div>
                )}

                {!afiliado && <p className="text-center text-gray-700">Busca un afiliado para dar de baja o reactivar</p>}
            </div>

            {/* ALERTA */}
            {alerta && <Alerta mensaje={alerta.mensaje} type={alerta.type} onClose={() => setAlerta(null)} />}

            {/* POPUPS */}
            {modoPopup === "baja" && (
                <PopupEditarAfiliado
                    titulo="Dar de baja afiliado"
                    descripcion="El afiliado dejará de estar activo."
                    descripcionValidacion="¿Seguro que deseas dar de baja este afiliado?"
                    textoConfirmar="Dar de baja"
                    onConfirm={handleBaja}
                    onCancel={() => setModoPopup(null)}
                />
            )}

            {modoPopup === "reactivar" && (
                <PopupEditarAfiliado
                    titulo="Reactivar afiliado"
                    descripcion="El afiliado volverá a estar activo."
                    descripcionValidacion="¿Deseas reactivar este afiliado?"
                    textoConfirmar="Reactivar"
                    onConfirm={handleReactivar}
                    onCancel={() => setModoPopup(null)}
                />
            )}
        </div>
    );
}
